import type { QueryClient, UseMutationOptions } from '@tanstack/react-query';
import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import {
  type FavoriteImageV1WorkspacesWorkspaceIdAssetsImagesImageIdFavoritePutError,
  type FavoriteImageV1WorkspacesWorkspaceIdAssetsImagesImageIdFavoritePutResponse,
  favoriteImageV1WorkspacesWorkspaceIdAssetsImagesImageIdFavoritePut,
  unfavoriteImageV1WorkspacesWorkspaceIdAssetsImagesImageIdFavoriteDelete,
} from '../../generated';
import {
  type BulkFavoriteImagesV1WorkspacesWorkspaceIdAssetsImagesFavoriteBulkPutData as BulkFavoriteData,
  type BulkFavoriteImagesV1WorkspacesWorkspaceIdAssetsImagesFavoriteBulkPutResponse as BulkFavoriteResponse,
  type BulkUnfavoriteImagesV1WorkspacesWorkspaceIdAssetsImagesFavoriteBulkDeleteResponse as BulkUnfavoriteResponse,
  bulkFavoriteImagesV1WorkspacesWorkspaceIdAssetsImagesFavoriteBulkPut as bulkFavorite,
  bulkUnfavoriteImagesV1WorkspacesWorkspaceIdAssetsImagesFavoriteBulkDelete as bulkUnfavorite,
} from '../../generated';

const SET_ASSET_FAVORITE_MUTATION_KEY = ['SetAssetFavorite'] as const;
const BULK_SET_ASSET_FAVORITE_MUTATION_KEY = ['BulkSetAssetFavorite'] as const;

const noop = () => {};

/** Tail of the in-flight request chain per asset, keyed workspace:image. */
const requestTails = new Map<string, Promise<unknown>>();

/**
 * Runs `request` only after the previous request for the same `key` has
 * settled, so requests reach the API in the order they were submitted.
 *
 * PUT and DELETE are each idempotent, but they are not commutative: if a user
 * favorites then unfavorites quickly and the DELETE overtakes the PUT, the
 * server settles on favorited — the opposite of the last click. Chaining per
 * asset makes submission order the execution order, so the final click always
 * decides. A rejected request does not break the chain.
 *
 * Exported for tests; callers should use `useSetAssetFavoriteMutation`.
 */
export function serializeAssetFavoriteRequest<T>(key: string, request: () => Promise<T>): Promise<T> {
  const previous = requestTails.get(key);
  const result = (previous ? previous.then(noop, noop) : Promise.resolve()).then(request);
  const tail = result.then(noop, noop);
  requestTails.set(key, tail);

  // Keep the map from growing without bound: drop the key once this request is
  // the last one still registered for it.
  tail.then(() => {
    if (requestTails.get(key) === tail) {
      requestTails.delete(key);
    }
  }, noop);

  return result;
}

/**
 * Every query whose payload carries `ImageView.favorite`. A surface missing
 * from this list keeps serving a stale flag: the optimistic overlay clears
 * when the mutation settles, and the heart snaps back to whatever the
 * un-refetched payload still says.
 *
 * `ListClassificationImages` is deliberately absent — it serves
 * `ExploreImageView`, which has no favorite flag.
 */
export const IMAGE_PAYLOAD_QUERY_KEYS = new Set([
  'GetImage',
  'GetImageById',
  'ListBenchmarkImages',
  'SearchCollectionImages',
  'SearchCollectionImagesInfinite',
  'SearchImages',
  'SearchImagesInfinite',
  'SearchPdpImages',
  'SearchPdpImagesInfinite',
]);

/**
 * Refetches every payload that carries `ImageView.favorite` after a toggle settles.
 *
 * On success the returned promise keeps the mutation pending until the refetch lands, so the optimistic overlay holds
 * the new value and the heart doesn't blink back to the stale payload. On error nothing changed on the server, so the
 * refetch runs in the background and the mutation settles right away: the overlay clears and the heart reverts
 * together with the error toast, instead of seconds later when every image list has refetched.
 */
export function refetchImagePayloads(queryClient: QueryClient, error: unknown) {
  const refetch = queryClient.invalidateQueries({
    predicate: (query) => IMAGE_PAYLOAD_QUERY_KEYS.has(String(query.queryKey[0])),
  });
  return error ? undefined : refetch;
}

export interface SetAssetFavoriteMutationVariables {
  workspace_id: string;
  image_id: string;
  /**
   * The desired end state of the flag, not a flip. The API splits it across
   * PUT (favorite) and DELETE (unfavorite), both idempotent, so a retry can
   * never land on the opposite of what the user clicked.
   */
  is_favorite: boolean;
}

export type SetAssetFavoriteMutation = FavoriteImageV1WorkspacesWorkspaceIdAssetsImagesImageIdFavoritePutResponse;

/**
 * Sets or unsets an asset's workspace-wide favorite flag.
 *
 * The flag is served on the asset payload (`ImageView.favorite`), so there is
 * no favorites list to patch: on settle the image lists are invalidated and
 * the server's value becomes the truth. Read the optimistic value with
 * `useAssetFavorites`, which overlays in-flight variables on the payload.
 *
 * @example
 * const { mutate: setFavorite } = useSetAssetFavoriteMutation();
 * setFavorite({ workspace_id, image_id, is_favorite: !isFavorite(asset) });
 */
export const useSetAssetFavoriteMutation = <
  TError = FavoriteImageV1WorkspacesWorkspaceIdAssetsImagesImageIdFavoritePutError,
  TContext = unknown,
>(
  options?: Omit<
    UseMutationOptions<SetAssetFavoriteMutation, TError, SetAssetFavoriteMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<SetAssetFavoriteMutation, TError, SetAssetFavoriteMutationVariables, TContext>({
    mutationKey: SET_ASSET_FAVORITE_MUTATION_KEY,
    mutationFn: ({ workspace_id, image_id, is_favorite }: SetAssetFavoriteMutationVariables) => {
      const call = is_favorite
        ? favoriteImageV1WorkspacesWorkspaceIdAssetsImagesImageIdFavoritePut
        : unfavoriteImageV1WorkspacesWorkspaceIdAssetsImagesImageIdFavoriteDelete;
      return serializeAssetFavoriteRequest(`${workspace_id}:${image_id}`, async () => {
        const { data } = await call({ path: { workspace_id, image_id }, throwOnError: true });
        return data;
      });
    },
    ...options,
    onSettled: (data, error, variables, context) => {
      options?.onSettled?.(data, error, variables, context);
      // Every heart reads `favorite` off an ImageView, so refetching the
      // queries that carry one is the whole cache reconciliation — there is no
      // favorites list to patch. The optimistic value is held by the pending
      // overlay in `useAssetFavorites` until these land, so a surface left
      // stale here would flip its heart back once the overlay clears.
      return refetchImagePayloads(queryClient, error);
    },
  });
};

export type BulkSetAssetFavoriteMutationVariables = BulkFavoriteData['path'] &
  BulkFavoriteData['body'] & {
    /** The desired end state for every image: PUT adds to favorites, DELETE removes. Both are idempotent. */
    is_favorite: boolean;
  };

/** Only the images whose state changed; images already in the requested state are skipped. */
export type BulkSetAssetFavoriteMutation = BulkFavoriteResponse | BulkUnfavoriteResponse;

async function bulkSetAssetFavorite({ workspace_id, is_favorite, ...body }: BulkSetAssetFavoriteMutationVariables) {
  const options = { path: { workspace_id }, body, throwOnError: true } as const;
  const { data } = is_favorite ? await bulkFavorite(options) : await bulkUnfavorite(options);
  return data;
}

/**
 * Sets or unsets the favorite flag of many assets of one workspace in one request. All or nothing: if any image is
 * out of scope the server changes none of them.
 *
 * Shares the optimistic overlay of `useAssetFavorites` with the single toggle, so hearts flip right away and revert
 * on their own if the request fails.
 *
 * @example
 * const { mutate: setFavorites } = useBulkSetAssetFavoriteMutation();
 * setFavorites({ workspace_id, image_ids: [...selectedIds], is_favorite: true });
 */
export const useBulkSetAssetFavoriteMutation = <TError = unknown, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<BulkSetAssetFavoriteMutation, TError, BulkSetAssetFavoriteMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<BulkSetAssetFavoriteMutation, TError, BulkSetAssetFavoriteMutationVariables, TContext>({
    mutationKey: BULK_SET_ASSET_FAVORITE_MUTATION_KEY,
    mutationFn: bulkSetAssetFavorite,
    ...options,
    onSettled: (data, error, variables, context) => {
      options?.onSettled?.(data, error, variables, context);
      // Same reconciliation as the single toggle: refetch every payload that carries `ImageView.favorite`
      return refetchImagePayloads(queryClient, error);
    },
  });
};

interface FavoritableAsset {
  id: string;
  favorite?: boolean;
}

interface PendingFavorite {
  imageIds: string[];
  isFavorite: boolean;
  submittedAt: number;
}

/**
 * Reads an asset's favorite state, overlaying any in-flight toggle on top of
 * the served `ImageView.favorite`.
 *
 * The overlay is the optimistic update: a pending toggle reads as its desired
 * end state, and when a request fails the mutation leaves `pending` so the
 * overlay disappears and the heart reverts on its own — no rollback branch to
 * get wrong. Single and bulk toggles share it.
 *
 * @example
 * const { isFavorite } = useAssetFavorites();
 * <Asset.Actions isFavorite={isFavorite(asset)} />
 */
export function useAssetFavorites() {
  const pendingSingle = useMutationState({
    filters: { mutationKey: SET_ASSET_FAVORITE_MUTATION_KEY, status: 'pending' },
    select: (mutation): PendingFavorite | undefined => {
      const variables = mutation.state.variables as SetAssetFavoriteMutationVariables | undefined;
      return variables
        ? { imageIds: [variables.image_id], isFavorite: variables.is_favorite, submittedAt: mutation.state.submittedAt }
        : undefined;
    },
  });
  const pendingBulk = useMutationState({
    filters: { mutationKey: BULK_SET_ASSET_FAVORITE_MUTATION_KEY, status: 'pending' },
    select: (mutation): PendingFavorite | undefined => {
      const variables = mutation.state.variables as BulkSetAssetFavoriteMutationVariables | undefined;
      return variables
        ? { imageIds: variables.image_ids, isFavorite: variables.is_favorite, submittedAt: mutation.state.submittedAt }
        : undefined;
    },
  });

  // Last write wins across single and bulk toggles, so the final click decides.
  const overlay = new Map<string, boolean>();
  for (const pending of [...pendingSingle, ...pendingBulk]
    .filter((item): item is PendingFavorite => !!item)
    .sort((a, b) => a.submittedAt - b.submittedAt)) {
    for (const imageId of pending.imageIds) {
      overlay.set(imageId, pending.isFavorite);
    }
  }

  const isFavorite = (asset: FavoritableAsset) => overlay.get(asset.id) ?? !!asset.favorite;

  return { isFavorite };
}
